/**
 * Telegram WebApp integration.
 *
 * A thin, defensive wrapper: the Mini App must also run in a plain browser
 * during development, so every capability is feature-detected rather than
 * assumed. `initDataUnsafe` is never used for identity — the raw `initData`
 * string is sent to the server, which verifies its signature.
 */

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe?: {
    user?: { id: number; first_name?: string; language_code?: string; photo_url?: string };
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  isExpanded: boolean;
  /** Bot API 8.0. Absent on older clients, which is a supported state. */
  isFullscreen?: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  safeAreaInset?: { top: number; bottom: number; left: number; right: number };
  contentSafeAreaInset?: { top: number; bottom: number; left: number; right: number };
  ready(): void;
  expand(): void;
  close(): void;
  /** Bot API 8.0. Resolves through `fullscreenChanged` / `fullscreenFailed`. */
  requestFullscreen?(): void;
  exitFullscreen?(): void;
  openInvoice(url: string, callback?: (status: string) => void): void;
  openLink(url: string, options?: { try_instant_view?: boolean }): void;
  openTelegramLink(url: string): void;
  onEvent(event: string, handler: (...args: unknown[]) => void): void;
  offEvent(event: string, handler: (...args: unknown[]) => void): void;
  BackButton: {
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
  MainButton: {
    setText(text: string): void;
    show(): void;
    hide(): void;
    enable(): void;
    disable(): void;
    showProgress(leaveActive?: boolean): void;
    hideProgress(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
    selectionChanged(): void;
  };
  /**
   * Telegram's own chrome. `setBackgroundColor` and `setHeaderColor` are Bot API
   * 6.1 (the header takes a hex only from 6.9), `setBottomBarColor` is 7.10.
   * They are declared optional because a browser has none of them — inside
   * Telegram they are always present and the version is the real gate. See
   * `atLeast`.
   */
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  setBottomBarColor?(color: string): void;
  /** Bot API 7.7. */
  disableVerticalSwipes?(): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export const webApp = (): TelegramWebApp | undefined => window.Telegram?.WebApp;

export const isInsideTelegram = (): boolean => Boolean(webApp()?.initData);

/** The raw signed payload. The server is the only thing that may trust it. */
export const getInitData = (): string => webApp()?.initData ?? '';

/**
 * The customer's Telegram avatar, for the profile tab.
 *
 * Read from `initDataUnsafe`, which is exactly the case that field is for: it
 * is unverified, so it may never decide who somebody is — but nothing here
 * depends on it being true. A wrong picture shows a wrong picture; identity
 * still comes from the signed `initData` the server checks, and this returns
 * undefined outside Telegram, which the caller has to handle anyway because
 * Telegram itself omits the field for anyone whose privacy settings hide it.
 */
export const getTelegramPhotoUrl = (): string | undefined =>
  webApp()?.initDataUnsafe?.user?.photo_url;

/**
 * Whether the client behind the SDK is new enough for a method.
 *
 * This is the check, not `?.`. `telegram-web-app.js` is served by telegram.org
 * and is always the current build, so every method it knows about exists on the
 * object no matter how old the client behind it is — optional chaining detects
 * the *script*, which is never missing, and tells us nothing about the client.
 * The real gate lives inside each method, where an unsupported call is a
 * `console.warn`, a `console.error`, or a thrown `WebAppMethodUnsupported`. So
 * the reported version has to be checked before the call, or the console fills
 * with errors for things we already knew were unavailable.
 *
 * Compared segment by segment and not as a number: 7.10 is newer than 7.7, and
 * `parseFloat` says the opposite. An unparseable or absent version reads as 0,
 * which means "do not call" — the right way to be wrong here.
 */
function atLeast(reported: string, required: string): boolean {
  const have = reported.split('.');
  const want = required.split('.');

  for (let i = 0; i < Math.max(have.length, want.length); i += 1) {
    const left = Number.parseInt(have[i] ?? '0', 10) || 0;
    const right = Number.parseInt(want[i] ?? '0', 10) || 0;
    if (left !== right) return left > right;
  }
  return true;
}

/**
 * Applies Telegram's theme.
 *
 * ThemeParams arrive as `bg_color`, `text_color` and so on; mapping them onto
 * `--tg-theme-*` variables lets the token layer inherit any theme the user has,
 * including custom ones, with no per-theme code.
 */
export function applyTheme(): void {
  const app = webApp();
  const root = document.documentElement;

  if (!app) {
    const dark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    root.dataset.theme = dark ? 'dark' : 'light';
    return;
  }

  root.dataset.theme = app.colorScheme;
  for (const [key, value] of Object.entries(app.themeParams ?? {})) {
    root.style.setProperty(`--tg-theme-${key.replace(/_/g, '-')}`, value);
  }

  applySafeArea();
  /*
   * The chrome is part of the same decision, not a startup step. Telegram keeps
   * painting outside the web view with whatever it was last told, so a flip that
   * repaints only the page leaves the previous scheme framing it — a near-black
   * band around a light app. This runs after `data-theme` is set, which is what
   * makes the colour it reads the new one.
   */
  applyChrome();
}

/**
 * Mirrors Telegram's insets onto the CSS variables the layout reads.
 *
 * Kept separate from the theme because they change independently and far more
 * often: entering fullscreen, rotating, and the client showing or hiding its
 * own header all move these numbers while the palette stays put. In fullscreen
 * Telegram draws its controls *over* the page, so `safeAreaInset.top` is the
 * only thing keeping the first heading out from under them.
 */
export function applySafeArea(): void {
  const app = webApp();
  const root = document.documentElement;
  if (!app) return;

  const safe = app.safeAreaInset;
  if (safe) {
    root.style.setProperty('--tg-safe-area-inset-top', `${safe.top}px`);
    root.style.setProperty('--tg-safe-area-inset-bottom', `${safe.bottom}px`);
  }

  const contentSafe = app.contentSafeAreaInset;
  if (contentSafe) {
    root.style.setProperty('--tg-content-safe-area-inset-top', `${contentSafe.top}px`);
    root.style.setProperty('--tg-content-safe-area-inset-bottom', `${contentSafe.bottom}px`);
  }
}

/**
 * The colour the app is actually painting its canvas with, as `#rrggbb`.
 *
 * Read back from the resolved custom property rather than repeated as a literal
 * here: `--surface-canvas` has a light value, a dark value, a
 * `prefers-color-scheme` value for the first frame, and an escape hatch that
 * binds it to the user's own Telegram theme. A second copy of any of those in
 * this file would be wrong the first time one of them moved, and wrong in the
 * one place nobody looks — outside the page.
 *
 * `getComputedStyle` also flushes the pending style change, so reading it right
 * after `data-theme` flips gives the new colour rather than the old one.
 */
function canvasColor(): string | null {
  // Guarded, not because a client lacks it, but because this module is also
  // loaded where the DOM is a stub: no computed style, nothing to paint either.
  const computed = window.getComputedStyle?.(document.documentElement);
  return computed ? toHexColor(computed.getPropertyValue('--surface-canvas')) : null;
}

/**
 * Telegram parses `#rgb` and `#rrggbb` and rejects everything else by throwing,
 * so whatever CSS hands back has to be normalised before it goes over the
 * bridge. Custom properties come back as authored — a hex today — but the
 * Telegram-theme escape hatch substitutes whatever the client supplied, which
 * we do not control. Anything unparseable leaves the chrome alone: a colour we
 * cannot vouch for is worse than Telegram's own.
 */
function toHexColor(value: string): string | null {
  const color = value.trim().toLowerCase();

  if (/^#[\da-f]{3}$/.test(color)) return color.replace(/^#(.)(.)(.)$/, '#$1$1$2$2$3$3');
  if (/^#[\da-f]{6}$/.test(color)) return color;

  const rgb = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/.exec(color);
  if (!rgb) return null;

  const channels = rgb.slice(1, 4).map((part) => Number(part).toString(16).padStart(2, '0'));
  return `#${channels.join('')}`;
}

type ChromeMethod = 'setBackgroundColor' | 'setHeaderColor' | 'setBottomBarColor';

function paint(app: TelegramWebApp, method: ChromeMethod, since: string, color: string): void {
  if (!atLeast(app.version, since)) return;

  try {
    app[method]?.(color);
  } catch {
    // A client that reports a version it does not honour is not worth a crash:
    // the page itself is already the right colour, only its frame is not.
  }
}

/**
 * Paints Telegram's own chrome with our canvas colour.
 *
 * Everything outside the web view belongs to Telegram, and its default is a
 * blue-grey (#212d3b-ish) that has nothing to do with this palette. It is
 * invisible until the page fails to cover it, and then it is very visible: in
 * the rubber band past the end of a scroll, above the header, and in the strip
 * below the app when the content is short — "если я листну то там будет синее
 * чето". No amount of CSS reaches it; only these three calls do.
 *
 * All three take our canvas rather than a keyword, so the seam is not a seam.
 */
export function applyChrome(): void {
  const app = webApp();
  if (!app) return;

  const color = canvasColor();
  if (!color) return;

  /*
   * The header is gated at 6.9 rather than 6.1 because 6.1 accepts only
   * `bg_color` / `secondary_bg_color` and throws on a hex — and neither keyword
   * is our colour, so on an older client the header stays Telegram's rather
   * than becoming a third shade. The bottom bar is 7.10 and is exactly the band
   * under the tab bar.
   */
  paint(app, 'setBackgroundColor', '6.1', color);
  paint(app, 'setHeaderColor', '6.9', color);
  paint(app, 'setBottomBarColor', '7.10', color);
}

export type FullscreenOutcome = 'fullscreen' | 'expanded';

/**
 * Detaches the listeners of a request that has not answered yet.
 *
 * The request is asynchronous and a client is free never to answer it at all,
 * so teardown has to be able to reach into a pending one. Without this, an
 * unmount mid-flight leaves two listeners attached to a long-lived object.
 */
let cancelPendingFullscreen: (() => void) | null = null;

/**
 * Asks for fullscreen, and settles for expanded.
 *
 * `requestFullscreen` is Bot API 8.0. On an older client "not fullscreen" is an
 * ordinary outcome rather than an error — but it has to be recognised *before*
 * the call: the method is on the object whatever the client is, and calling it
 * anyway is a `console.error` plus a thrown `WebAppMethodUnsupported` on every
 * single launch. That is a large share of the noise in the owner's console.
 *
 * Even on 8.0 the request is asynchronous and can be refused —
 * `fullscreenFailed` carries `UNSUPPORTED` or `ALREADY_FULLSCREEN` — so the
 * expanded layout has to be a real fallback and not a transient state.
 *
 * `expand()` is called first and unconditionally: it is 6.0, it costs nothing,
 * it is what keeps the app off the bottom half of the screen if the request
 * never resolves, and Telegram ignores it once fullscreen is granted.
 */
export function requestFullscreen(): Promise<FullscreenOutcome> {
  const app = webApp();
  app?.expand();

  if (!app?.requestFullscreen) return Promise.resolve('expanded');
  if (!atLeast(app.version, '8.0')) return Promise.resolve('expanded');
  if (app.isFullscreen) return Promise.resolve('fullscreen');

  return new Promise((resolve) => {
    let settled = false;
    const finish = (outcome: FullscreenOutcome) => {
      if (settled) return;
      settled = true;
      app.offEvent('fullscreenChanged', onChanged);
      app.offEvent('fullscreenFailed', onFailed);
      cancelPendingFullscreen = null;
      applySafeArea();
      resolve(outcome);
    };

    const onChanged = () => finish(app.isFullscreen ? 'fullscreen' : 'expanded');
    const onFailed = () => finish('expanded');

    app.onEvent('fullscreenChanged', onChanged);
    app.onEvent('fullscreenFailed', onFailed);
    cancelPendingFullscreen = () => finish('expanded');

    try {
      app.requestFullscreen?.();
    } catch {
      // A client that advertises the method but throws is still just expanded.
      finish('expanded');
    }
  });
}

/** Prepares the WebView: chrome, expand, theme, and keep all three in sync. */
export function initTelegram(): () => void {
  const app = webApp();
  /*
   * Theme first, and with it the chrome, because `ready()` is what tells the
   * client to drop its loading placeholder and show the web view. Colours set
   * after that point are a visible correction; set before it, the app is simply
   * the right colour when it appears.
   */
  applyTheme();

  if (!app) return () => {};

  app.ready();
  // Fire and forget: the layout is correct in either outcome, so nothing waits.
  void requestFullscreen();

  /*
   * A store must not close on a pull.
   *
   * The page itself never scrolls — `.app__scroll` does — so a downward drag
   * that begins with that container already at the top is handed to Telegram as
   * its dismiss gesture. Halfway through checkout that is an abandoned order,
   * and the customer's fault for none of it. 7.7; below that the gesture cannot
   * be disabled at all and the call is only noise.
   */
  if (atLeast(app.version, '7.7')) {
    try {
      app.disableVerticalSwipes?.();
    } catch {
      // Nothing to fall back to — the gesture stays live, the app still works.
    }
  }

  const onThemeChanged = () => applyTheme();
  const onSafeAreaChanged = () => applySafeArea();
  const onViewportChanged = () => {
    /*
     * Two heights, because the difference between them is the keyboard.
     *
     * `viewportStableHeight` ignores transient changes, so it is what the shell
     * is anchored to; `viewportHeight` is what is actually visible right now.
     * When the on-screen keyboard opens only the second one shrinks, and the
     * gap is the exact number a field needs in order to sit above the keys
     * instead of behind them. Telegram gives no other way to measure it —
     * `100dvh` inside a Telegram WebView does not react to the keyboard.
     */
    const root = document.documentElement;
    root.style.setProperty('--tg-viewport-height', `${app.viewportHeight}px`);
    root.style.setProperty('--tg-viewport-stable-height', `${app.viewportStableHeight}px`);
    root.style.setProperty(
      '--tg-keyboard-height',
      `${Math.max(0, app.viewportStableHeight - app.viewportHeight)}px`,
    );
  };

  app.onEvent('themeChanged', onThemeChanged);
  app.onEvent('viewportChanged', onViewportChanged);
  /*
   * Both inset events matter and they are not the same one. `safeAreaChanged`
   * is the device's notch and home bar; `contentSafeAreaChanged` is Telegram's
   * own header, which appears and disappears with fullscreen. Listening to only
   * one leaves the header tucked under whichever the other describes.
   */
  app.onEvent('safeAreaChanged', onSafeAreaChanged);
  app.onEvent('contentSafeAreaChanged', onSafeAreaChanged);
  app.onEvent('fullscreenChanged', onSafeAreaChanged);
  onViewportChanged();

  return () => {
    cancelPendingFullscreen?.();
    app.offEvent('themeChanged', onThemeChanged);
    app.offEvent('viewportChanged', onViewportChanged);
    app.offEvent('safeAreaChanged', onSafeAreaChanged);
    app.offEvent('contentSafeAreaChanged', onSafeAreaChanged);
    app.offEvent('fullscreenChanged', onSafeAreaChanged);
  };
}

/**
 * Haptics, 6.1, gated like everything else — and this is the gate that matters
 * most. It is the most frequently called API in the app: feedback is on press,
 * so every tap on every row goes through here. Ungated on an older client that
 * is not one line in the console, it is one line per touch.
 */
const haptics = (): TelegramWebApp['HapticFeedback'] => {
  const app = webApp();
  return app && atLeast(app.version, '6.1') ? app.HapticFeedback : undefined;
};

export const haptic = {
  impact(style: 'light' | 'medium' | 'heavy' = 'light'): void {
    haptics()?.impactOccurred(style);
  },
  success(): void {
    haptics()?.notificationOccurred('success');
  },
  error(): void {
    haptics()?.notificationOccurred('error');
  },
  select(): void {
    haptics()?.selectionChanged();
  },
};

/**
 * Opens a Telegram invoice.
 *
 * Falls back to a normal link outside Telegram so the flow is still testable in
 * a browser, where `openInvoice` does not exist.
 *
 * A client older than 6.1 takes the same fallback, and has to: there the method
 * exists but throws, and the throw escapes from inside the promise executor —
 * so the promise never settles and the pay button spins for ever. A link that
 * opens is a poor checkout; a button that never comes back is not one at all.
 */
export function openInvoice(url: string): Promise<string> {
  return new Promise((resolve) => {
    const app = webApp();
    if (!app?.openInvoice || !atLeast(app.version, '6.1')) {
      window.open(url, '_blank', 'noopener');
      resolve('unsupported');
      return;
    }
    app.openInvoice(url, (status) => resolve(status));
  });
}

export function useBackButton(visible: boolean, handler: () => void): () => void {
  const app = webApp();
  if (!app) return () => {};

  if (visible) {
    app.BackButton.onClick(handler);
    app.BackButton.show();
  } else {
    app.BackButton.hide();
  }

  return () => {
    app.BackButton.offClick(handler);
    app.BackButton.hide();
  };
}
